import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';

import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import JBImage from 'components/essentials/JBImage';
import MobileSlider from 'components/consumer/MobileSlider/MobileSlider';
import useScreen from 'hooks/useScreen';
import useIsBrowser from 'hooks/useIsBrowser';
import NukaCarousel from 'components/external/NukaCarousel';

const CDNImageManager = props => {
  if (props.src && props.src.includes('images.ctfassets.net')) {
    return (
      <img
        height={props.height}
        width={props.width}
        src={props.src}
        alt={props.alt}
      />
    );
  }
  return <JBImage {...props} />;
};

CDNImageManager.propTypes = {
  alt: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  src: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const SliderStandard = props => {
  const containerRef = useRef(null);
  const [beforeSlider, updateBeforeSlider] = useState(0);
  const [afterSlider, updateAfterSlider] = useState(0);

  let { imageHeight, imageWidth } = props;

  const {
    children,
    finalVisibleSlides,
    hideArrowsIfAllSlidesVisible = false,
    imageArray,
    navType,
    slideIndex,
    updateImageArraySize,
  } = props;

  let finalSlides = imageArray || children;
  let visibleSlides = 0;

  useEffect(() => {
    // Forward scroll
    if (beforeSlider) {
      if (beforeSlider < afterSlider) {
        if (afterSlider > finalSlides.length - afterSlider) {
          if (updateImageArraySize) {
            updateImageArraySize(afterSlider);
          }
        }
      } else if (beforeSlider > afterSlider) {
        if (afterSlider === finalSlides.length - visibleSlides) {
          if (updateImageArraySize) {
            updateImageArraySize(afterSlider);
          }
        }
      }
    }
  }, [
    afterSlider,
    beforeSlider,
    finalSlides.length,
    updateImageArraySize,
    visibleSlides,
  ]);

  const sizes = null;

  const isBrowser = useIsBrowser();
  const { isMobile } = useScreen();

  const isLargeDesktopDevice = useMediaQuery({
    query: '(min-width: 1281px)',
  });

  // Assume this is a small mobile device by default, until mounted in browser
  const isLargeDesktop = isBrowser ? isLargeDesktopDevice : false;
  const isMobileTabletOrSmallDesktop = !isLargeDesktop;

  // Responsive defaults only overridden if values are set
  if (isLargeDesktop) {
    visibleSlides =
      finalVisibleSlides && finalVisibleSlides < 4
        ? finalVisibleSlides
        : finalSlides.length < 4
        ? finalSlides.length
        : 4;
    visibleSlides = parseInt(visibleSlides, 10);
    if (parseInt(visibleSlides, 10) === 4) {
      imageHeight = 335;
    }
  }

  if (isMobileTabletOrSmallDesktop) {
    imageWidth = 320;
    imageHeight = 320;
    visibleSlides = finalSlides.length < 3 ? finalSlides.length : 3;
  }

  if (isMobile) {
    imageWidth = 720;
    imageHeight = 720;
    visibleSlides = 2;
  }

  if (!finalSlides.length) return null;

  if (finalSlides && finalSlides.length === 1) {
    return (
      <div
        className="mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover [&_img]:w-auto [&_img]:max-h-[450px] [&_img]:object-contain"
        ref={containerRef}
      >
        <CDNImageManager
          key="1"
          src={finalSlides[0].src || finalSlides[0].url || finalSlides[0].val}
          trim="auto"
          lazy
          sizes="100vw"
        />
      </div>
    );
  }

  // Workaround for wraparound breaking when number of slides < 2 * slidesToShow
  // Only checking for max visible slides 4
  if (finalSlides.length < 8 && finalSlides.length > visibleSlides) {
    while (finalSlides.length < 8) {
      finalSlides = finalSlides.concat(finalSlides);
    }
  }

  const hideNav = visibleSlides >= finalSlides.length;

  return (
    <div
      className="mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover [&_.slider-control-bottomcenter]:w-full"
      ref={containerRef}
    >
      {isMobile ? (
        <MobileSlider
          slides={finalSlides}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          sizes={sizes}
        />
      ) : (
        <NukaCarousel
          slideIndex={slideIndex || 0}
          slidesToShow={visibleSlides}
          cellSpacing={20}
          dragging
          wrapAround={finalSlides.length > visibleSlides}
          width="100%"
          speed={200}
          renderCenterLeftControls={({ previousSlide }) => (
            <button
              className={classNames(
                'group/SliderButton w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white left-2 transform-[translateX(-50%)]',
                '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
                'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
                {
                  '!hidden':
                    hideArrowsIfAllSlidesVisible && finalSlides.length <= 3,
                  'xl:!hidden':
                    hideArrowsIfAllSlidesVisible && finalSlides.length === 4,
                }
              )}
              aria-label="Previous slide"
              onClick={previousSlide}
              type="button"
            >
              {' '}
              <IconArrowLeftWhite />
            </button>
          )}
          renderCenterRightControls={({ nextSlide }) => (
            <button
              className={classNames(
                'group/SliderButton w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white right-2 transform-[translateX(50%)]',
                '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
                'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
                {
                  '!hidden':
                    hideArrowsIfAllSlidesVisible && finalSlides.length <= 3,
                  'xl:!hidden':
                    hideArrowsIfAllSlidesVisible && finalSlides.length === 4,
                }
              )}
              aria-label="Next slide"
              onClick={nextSlide}
              type="button"
            >
              <IconArrowRightWhite />
            </button>
          )}
          renderBottomCenterControls={controlProps => {
            if (navType === 'Button' || hideNav) return null;
            const slideCountArray = [...Array(controlProps.slideCount).keys()];
            return (
              <div
                className={classNames(
                  'absolute block bottom-[-45px] w-full p-0 m-0 list-none text-center',
                  '[&_button]:border-0 [&_button]:rounded-[50%] [&_button]:h-2 [&_button]:!w-2 [&_button]:bg-[#0eb9cb] [&_button]:!m-1 [&_button]:relative [&_button]:inline-block',
                  '[&_button:disabled]:bg-gray-light5',
                  '[&_button_span]:inline-block [&_button_span]:h-5 [&_button_span]:w-full [&_button_span]:max-w-[30px]'
                )}
              >
                {slideCountArray.map((slide, index) => (
                  <button
                    key={slide?.src ?? index}
                    disabled={controlProps.currentSlide === index}
                    onClick={() => controlProps.goToSlide(index)}
                    type="button"
                  >
                    <span />
                  </button>
                ))}
              </div>
            );
          }}
          afterSlide={slideCount => {
            updateAfterSlider(slideCount);
          }}
          beforeSlide={slideCount => {
            updateBeforeSlider(slideCount);
          }}
          heightMode="first"
          initialSlideWidth={imageWidth}
        >
          {finalSlides.map(image => (
            <CDNImageManager
              key={image.value || image.src || image.url}
              src={image.value || image.src || image.url}
              width={imageWidth}
              height={imageHeight}
              trim="auto"
              sizes={sizes}
              lazy
              alt=""
            />
          ))}
        </NukaCarousel>
      )}
    </div>
  );
};

SliderStandard.propTypes = {
  children: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      url: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  finalVisibleSlides: PropTypes.number,
  hideArrowsIfAllSlidesVisible: PropTypes.bool,
  imageArray: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      url: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  imageHeight: PropTypes.string,
  imageWidth: PropTypes.string,
  navType: PropTypes.string,
  slideIndex: PropTypes.number,
  updateImageArraySize: PropTypes.func,
};

export default SliderStandard;
